import React, { useEffect, useRef, useState } from 'react';
import { Button, Spin, Tag, message, Progress } from 'antd';
import { LeftOutlined, RightOutlined, DownloadOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

interface PptSlideViewerProps {
  pptProjectId: string;
  artifactName: string;
  onConfirm?: () => void;
}

export default function PptSlideViewer({ pptProjectId, artifactName, onConfirm }: PptSlideViewerProps) {
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<any>(null);
  const [slides, setSlides] = useState<any[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [promptPhase, setPromptPhase] = useState('');
  const [promptProgress, setPromptProgress] = useState(0);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [regeneratingSlideId, setRegeneratingSlideId] = useState<string | null>(null);
  const [messageApi, ctx] = message.useMessage();
  const pollRef = useRef<any>(null);

  useEffect(() => {
    loadProject();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pptProjectId]);

  useEffect(() => {
    if (!slides.length) return;
    const token = localStorage.getItem('auth-storage');
    let t = '';
    try { t = JSON.parse(token || '{}')?.state?.token || ''; } catch {}
    const urls: Record<string, string> = {};
    slides.forEach((s: any) => {
      if (s.assetId) urls[s.assetId] = `/api/ppt-assets/${s.assetId}/view?token=${encodeURIComponent(t)}&v=${encodeURIComponent(s.updatedAt || s.assetId)}`;
    });
    setPreviewUrls(urls);
  }, [slides]);

  const slide = slides[currentIdx];
  const currentPreviewUrl = slide?.assetId ? previewUrls[slide.assetId] : '';

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [currentIdx, slide?.id, slide?.assetId, currentPreviewUrl]);

  async function loadProject() {
    setLoading(true);
    const fetchState = async () => {
      try {
        const r = await api.get(`/ppt-projects/${pptProjectId}/full-state`);
        const pg = r.data?.data?.promptGeneration;
        const sorted = (r.data?.slides || []).slice().sort((a: any, b: any) => Number(a.no) - Number(b.no));
        setSlides(sorted);
        // generationMode 以 data.generationMode 为准（旧版 project 对象里没有该字段）
        setProject({ ...(r.data?.project || {}), generationMode: r.data?.data?.generationMode || r.data?.project?.generationMode || 'separated' });
        setPromptPhase(pg?.phaseText || '');
        setPromptProgress(pg?.progress || 0);
        if (sorted.length > 0 && pg?.status !== 'running') {
          setLoading(false);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        } else if (pg?.status === 'error') {
          setLoading(false);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e: any) {
        messageApi.error('加载PPT项目失败');
        setLoading(false);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    };
    await fetchState();
    if (loading) {
      pollRef.current = setInterval(fetchState, 3000);
    }
  }

  async function pollSlidesUntilDone(targetSlideId?: string) {
    await new Promise<void>((resolve, reject) => {
      const check = setInterval(async () => {
        try {
          const r = await api.get(`/ppt-projects/${pptProjectId}/full-state`);
          const sls = (r.data?.slides || []).slice().sort((a: any, b: any) => Number(a.no) - Number(b.no));
          setSlides(sls);
          const targets = targetSlideId ? sls.filter((s: any) => String(s.id) === String(targetSlideId)) : sls;
          const allDone = targets.length > 0 && targets.every((s: any) => s.status === 'preview_done' || s.status === 'image_done');
          const hasError = targets.some((s: any) => s.status === 'error');
          if (allDone) { clearInterval(check); resolve(); }
          else if (hasError) { clearInterval(check); reject(new Error(targetSlideId ? '当前页面生成失败' : '部分页面生成失败')); }
        } catch { clearInterval(check); reject(new Error('查询状态失败')); }
      }, 2000);
      setTimeout(() => { clearInterval(check); reject(new Error('生图超时')); }, 300000);
    });
  }

  async function confirmAndGenerate() {
    if (!onConfirm) return;
    setGeneratingImages(true);
    const hide = messageApi.loading({ content: '正在为全部页面生成PPT图片…', key: 'ppt-gen', duration: 0 });
    try {
      await api.post(`/ppt-projects/${pptProjectId}/generate-images`, {});
      await pollSlidesUntilDone();
      messageApi.success({ content: '全部页面图片已生成', key: 'ppt-gen' });
      onConfirm();
    } catch (e: any) {
      messageApi.error({ content: e.message || '生图失败', key: 'ppt-gen' });
    } finally {
      setGeneratingImages(false);
      hide();
    }
  }

  async function regenerateCurrentSlide() {
    if (!slide?.id) return;
    setRegeneratingSlideId(String(slide.id));
    setImageLoaded(false);
    setImageError(false);
    messageApi.loading({ content: `正在重新生成第 ${slide.no || currentIdx + 1} 页…`, key: 'ppt-slide-gen', duration: 0 });
    try {
      await api.post(`/ppt-projects/${pptProjectId}/slides/${slide.id}/generate-image`, {});
      await pollSlidesUntilDone(String(slide.id));
      messageApi.success({ content: `第 ${slide.no || currentIdx + 1} 页已重新生成`, key: 'ppt-slide-gen' });
    } catch (e: any) {
      messageApi.error({ content: e.message || '当前页重新生成失败', key: 'ppt-slide-gen' });
    } finally {
      setRegeneratingSlideId(null);
    }
  }

  // 烘焙模式：文字已直接画进图片，不再叠加文字层（避免与图内文字重复）
  const isBakedMode = project?.generationMode === 'baked';
  const textItems = slide && !isBakedMode ? getTextItems(slide) : [];
  const allImaged = slides.length > 0 && slides.every((s: any) => s.status === 'preview_done');
  const doneCount = slides.filter((s: any) => s.status === 'preview_done' || s.status === 'image_done').length;
  const imagingCount = slides.filter((s: any) => s.status === 'image_generating').length;
  const imagePercent = slides.length ? Math.round((doneCount / slides.length) * 100) : 0;
  const slideProgress = typeof slide?.generation?.progress === 'number' ? slide.generation.progress : (slide?.status === 'preview_done' ? 100 : 0);

  function getTextItems(slide: any) {
    if (Array.isArray(slide?.textItems) && slide.textItems.length) return slide.textItems;
    const text = slide?.text || '';
    const lines = text.split('\n').filter(Boolean);
    return lines.map((line: string, i: number) => ({
      text: line.replace(/^[•\-]\s*/, ''),
      x: i === 0 ? (slide?.x ?? 7) : 58,
      y: i === 0 ? (slide?.y ?? 11) : 18 + i * 12,
      width: i === 0 ? (slide?.width ?? 42) : 32,
      fontSize: i === 0 ? (slide?.size || 28) : 16,
      color: i === 0 ? '#0f172a' : '#334155',
    }));
  }

  async function downloadPptx() {
    try {
      messageApi.loading({ content: '正在生成PPTX…', key: 'pptx', duration: 0 });
      await api.post(`/ppt-projects/${pptProjectId}/build-pptx`, { selectedSlideIds: slides.filter((s: any) => s.assetId).map((s: any) => s.id) });
      const r = await api.post(`/ppt-projects/${pptProjectId}/download-ticket`, {});
      if (r.data?.url) {
        const a = document.createElement('a');
        a.href = r.data.url;
        a.download = `${artifactName}.pptx`;
        a.click();
        messageApi.success({ content: 'PPTX下载已开始', key: 'pptx' });
      }
    } catch (e: any) {
      messageApi.error({ content: e?.response?.data?.message || '生成PPTX失败', key: 'pptx' });
    }
  }

  if (loading || (slides.length === 0 && promptPhase)) return (
    <div className="adc-ppt-loading-shell">
      {ctx}
      <Spin size="large" />
      <h3>{promptPhase || '正在加载PPT项目…'}</h3>
      <Progress percent={promptProgress || 8} status="active" />
      <p>AI 正在生成每页提示词和页面结构，请稍候。</p>
    </div>
  );

  if (!slides.length) return <div className="adc-ppt-empty">暂无页面数据</div>;

  return <div className="adc-ppt-viewer">
    {ctx}
    <div className="adc-ppt-toolbar">
      <div className="adc-ppt-title-block">
        <strong>{artifactName}</strong>
        <span>{project?.title || 'PPT 汇报大纲'} · 共 {slides.length} 页 · {isBakedMode ? '文字烘焙' : '文字分离'}</span>
      </div>
      <div className="adc-ppt-toolbar-actions">
        <Tag color={allImaged ? 'green' : 'cyan'}>{doneCount}/{slides.length} 页已出图</Tag>
        {!allImaged && <Progress type="circle" percent={imagePercent} size={42} />}
        {allImaged && <Button icon={<DownloadOutlined />} onClick={downloadPptx}>下载 PPTX</Button>}
        {allImaged && onConfirm && <Button type="primary" onClick={() => onConfirm()}>确认进入成果确认</Button>}
        {!allImaged && imagingCount > 0 && <span className="adc-ppt-live-tip">正在后台逐页生成图片（{imagingCount} 页）</span>}
        {!allImaged && imagingCount === 0 && !generatingImages && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={confirmAndGenerate}>生成全部页面图片</Button>
        )}
      </div>
    </div>

    <div className="adc-ppt-rail">
      {slides.map((s: any, i: number) => (
        <button type="button" key={s.id} onClick={() => setCurrentIdx(i)} className={`adc-ppt-thumb ${i === currentIdx ? 'active' : ''} ${s.status || ''}`}>
          <strong>第 {s.no} 页</strong>
          <span>{s.status === 'preview_done' ? '已生成' : s.status === 'image_generating' ? '生成中' : s.status === 'error' ? '失败' : '待生成'}</span>
        </button>
      ))}
    </div>

    <div className="adc-ppt-main">
      <div className="adc-ppt-slide-head">
        <div>
          <strong>第 {slide?.no || currentIdx + 1} 页预览</strong>
          <span>{slide?.status === 'preview_done' ? '图片已加载，可切换页面或单独重生成。' : slide?.status === 'image_generating' ? '当前页图片生成中，请等待加载完成。' : '当前页还没有图片，可生成全部或单独生成本页。'}</span>
        </div>
        <Button icon={<ReloadOutlined />} loading={regeneratingSlideId === String(slide?.id)} onClick={regenerateCurrentSlide}>重新生成当前页</Button>
      </div>
      <div className="adc-ppt-canvas" key={slide?.id || currentIdx}>
        {currentPreviewUrl ? (
          <div className="adc-ppt-image-wrap">
            {!imageLoaded && !imageError && <div className="adc-ppt-image-loading"><Spin /><strong>正在加载第 {slide?.no || currentIdx + 1} 页图片</strong><Progress percent={Math.max(slideProgress, imagePercent)} status="active" /></div>}
            {imageError && <div className="adc-ppt-image-loading"><strong>图片加载失败</strong><span>请点击“重新生成当前页”或稍后再试。</span></div>}
            <img
              key={`${slide?.id}-${slide?.assetId}`}
              src={currentPreviewUrl}
              alt={`第 ${slide?.no || currentIdx + 1} 页`}
              onLoad={() => setImageLoaded(true)}
              onError={() => { setImageError(true); setImageLoaded(false); }}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
            {imageLoaded && textItems.map((item: any, i: number) => (
              <div key={i} className="adc-ppt-text-layer" style={{
                left: `${item.x ?? 7}%`, top: `${item.y ?? 8}%`, width: `${item.width ?? 36}%`,
                fontSize: item.fontSize || 16, color: item.color || '#0f172a', fontWeight: i === 0 ? 800 : 600,
              }}>{item.text}</div>
            ))}
          </div>
        ) : (
          <div className="adc-ppt-image-loading adc-ppt-image-placeholder">
            <strong>第 {slide?.no || currentIdx + 1} 页正在等待图片</strong>
            <span>{slide?.status === 'image_generating' ? '正在生成图片，完成后会自动显示。' : slide?.status === 'prompt_ready' ? '提示词已完成，等待发起生图。' : slide?.status || '未知状态'}</span>
            {(slide?.status === 'image_generating' || regeneratingSlideId === String(slide?.id)) && <Progress percent={slideProgress} status="active" />}
          </div>
        )}
      </div>
    </div>

    <div className="adc-ppt-footer-nav">
      <Button size="large" icon={<LeftOutlined />} disabled={currentIdx === 0} onClick={() => setCurrentIdx(i => i - 1)}>上一页</Button>
      <span>{currentIdx + 1} / {slides.length}</span>
      <Button size="large" icon={<RightOutlined />} disabled={currentIdx >= slides.length - 1} onClick={() => setCurrentIdx(i => i + 1)}>下一页</Button>
    </div>
  </div>;
}
